import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { analytics, AnalyticsEvents } from "common/analytics";
import { useBottomBarFormDialog } from "common/components/useBottomBarFormDialog";
import { useTranslation } from "common/locales";
import { M2_PER_HECTARE } from "common/utils/area";
import {
  computeDefaultResidual,
  COVER_MAX,
  COVER_MIN,
  dmUtilisedTotalKg,
  leafZoneForDryMatter,
} from "common/utils/grazing-logic";
import FieldIcon from "features/fieldList/FieldIcon";
import {
  EventType,
  Field,
  getDemandFromArray,
  getTotalAnimalCountFromArray,
  useAnimalGroup,
  useAnimalGroupMoveEvents,
  useFields,
  useStockClassesByIds,
} from "model";
import { useActiveFeedBudgetDemand } from "model/useActiveFeedBudgetDemand";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createOneReadingWithoutFetchWithInvalidation,
  deleteAllTilesForField,
  invalidateFieldData,
} from "services";
import { moveToShed } from "services/readingOperations";
import { useUIStore } from "stores";
import { moveAnimalsIn } from "./moveAnimalsIn";

type Mode = "fieldToField" | "fieldToShed" | "shedToField";

const FIELD_THUMB_SIZE = 56;

const NAME_COLOR = "#1C4321"; // dahlia dark green — field names
const LEAVING_ACCENT = "#A48137"; // daisy amber — the "leaving" column
const ARRIVING_ACCENT = "#2E7831"; // dahlia green — the "arriving" column

// Leaving (post-grazing) should sit clearly below arriving (pre-grazing); warn
// if the gap is smaller than this.
const SIGNIFICANT_GAP = 300;

// Inline validation message under a cover input.
function CoverMessage({
  text,
  severity,
}: {
  text: string;
  severity: "error" | "warning";
}) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        mt: 0.5,
        lineHeight: 1.3,
        color: severity === "error" ? "error.main" : LEAVING_ACCENT,
      }}
    >
      {text}
    </Typography>
  );
}

// Colour-coded caption above each cover input — reinforces the left/right
// distinction (leaving vs arriving) without heavy coloured input borders.
const coverLabelSx = (accent: string) =>
  ({
    display: "block",
    textAlign: "center",
    color: accent,
    fontWeight: 600,
    fontSize: "0.75rem",
    mb: 0.5,
  } as const);

// Small leaf-stage badge shown under a cover value so a wrong/empty number
// reads visibly wrong (e.g. a low residual shows amber "Residual · L1–2").
function LeafChip({ cover }: { cover: number | "" }) {
  const zone = leafZoneForDryMatter(cover);
  if (!zone) return null;
  return (
    <Box
      sx={{
        alignSelf: "center",
        bgcolor: zone.bg,
        color: zone.tc,
        px: 1,
        py: 0.25,
        borderRadius: "6px",
        fontSize: "0.7rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {zone.label}
    </Box>
  );
}

// Soft-tinted pill button (inside the cover input) to apply the target cover.
function TargetChip({
  label,
  accent,
  bg,
  hoverBg,
  onClick,
}: {
  label: string;
  accent: string;
  bg: string;
  hoverBg: string;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        flexShrink: 0,
        cursor: "pointer",
        border: "none",
        fontFamily: "inherit",
        bgcolor: bg,
        color: accent,
        fontSize: "0.7rem",
        fontWeight: 600,
        px: 0.75,
        py: 0.375,
        borderRadius: "8px",
        whiteSpace: "nowrap",
        "&:hover": { bgcolor: hoverBg },
      }}
    >
      {label}
    </Box>
  );
}

// Field outline thumbnail with a neutral placeholder when no geometry exists.
function FieldThumb({
  geom,
  dryMatterNow,
}: {
  geom?: Field["geom"];
  dryMatterNow?: number;
}) {
  if (!geom) {
    return (
      <Box
        sx={{
          width: FIELD_THUMB_SIZE,
          height: FIELD_THUMB_SIZE,
          borderRadius: 2,
          bgcolor: "#EDEDED",
        }}
      />
    );
  }
  return (
    <FieldIcon
      geom={geom}
      dryMatterNow={dryMatterNow ?? 0}
      size={FIELD_THUMB_SIZE}
    />
  );
}

// The "shed" endpoint of a move (no cover input).
function ShedNode({ label }: { label: string }) {
  return (
    <Stack alignItems="center" spacing={1} sx={{ width: "100%" }}>
      <Box
        sx={{
          width: FIELD_THUMB_SIZE,
          height: FIELD_THUMB_SIZE,
          borderRadius: 2,
          bgcolor: "#F1F0EC",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <HomeIcon sx={{ color: "text.secondary", fontSize: 32 }} />
      </Box>
      <Typography variant="subtitle2" sx={{ textAlign: "center" }}>
        {label}
      </Typography>
    </Stack>
  );
}

export default function UpdateBiomassAfterMoveInDialog() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();

  const moveAnimalsDialogDetails = useUIStore(
    (state) => state.moveAnimalsDialogDetails
  );
  const closeMoveOutEventDialog = useUIStore(
    (state) => state.closeMoveOutEventDialog
  );

  const initialFromField = moveAnimalsDialogDetails.currentFieldWithAnimals;
  const toField = moveAnimalsDialogDetails.newFieldWithAnimals;
  const isMoveToShed = moveAnimalsDialogDetails.moveToShed ?? false;

  // Derive mode
  const mode: Mode = isMoveToShed
    ? "fieldToShed"
    : initialFromField && toField
    ? "fieldToField"
    : "shedToField";

  // For fieldToField and shedToField, use destination's animal group
  // For fieldToShed, use source's animal group
  const animalGroupId =
    mode === "fieldToShed"
      ? initialFromField?.animalGroup
      : toField?.animalGroup;
  const { data: allFields = [] } = useFields();
  const { data: animalGroup } = useAnimalGroup(animalGroupId);

  const postGrazingTarget = animalGroup?.postGrazingTarget;

  // Group's stock classes drive both head count and daily feed demand.
  const { data: groupStockClasses = [] } = useStockClassesByIds(
    animalGroup?.stockClasses
  );
  const headCount = getTotalAnimalCountFromArray(groupStockClasses);
  const feedBudgetDemand = useActiveFeedBudgetDemand();
  // Current daily DM demand (kg DM/day): prefer the active feed budget, fall
  // back to the stock classes' configured consumption.
  const dailyDemandKgDay =
    feedBudgetDemand.get(animalGroupId ?? "") ??
    getDemandFromArray(groupStockClasses);

  // Fields that belong to the same animal group as the destination (for from-field autocomplete)
  const groupFields = useMemo(
    () =>
      toField?.animalGroup
        ? allFields.filter(
            (f) =>
              f.animalGroup === toField.animalGroup && f.guid !== toField.guid
          )
        : [],
    [allFields, toField]
  );

  const [moveDate, setMoveDate] = useState<Date | null>(new Date());
  const [dateOpen, setDateOpen] = useState(false);
  const [fromField, setFromField] = useState<Field | null>(
    initialFromField ?? null
  );
  const [fromAnchor, setFromAnchor] = useState<HTMLElement | null>(null);
  const [postGrazingCover, setPostGrazingCover] = useState<number | "">("");
  const [preGrazingCover, setPreGrazingCover] = useState<number | "">(
    toField?.dryMatterNow ?? ""
  );
  const [saving, setSaving] = useState(false);
  // Whether the user has manually touched the leaving cover (stops the default
  // from clobbering their input once the target loads / the field changes).
  const postCoverEdited = useRef(false);

  // Field A's actual move-in (pre-grazing) cover: from its open grazing event in
  // the group's move history (same source RecordsPage uses). This is the correct
  // baseline for "DM utilised", not the field's latest reading.
  const { data: fromMoveEvents = [] } = useAnimalGroupMoveEvents(
    fromField?.animalGroup
  );
  const fromOpenEvent = useMemo(() => {
    if (!fromField) return null;
    return (
      fromMoveEvents
        .filter((e) => e.fieldGuid === fromField.guid && !e.dateTimeOut)
        .sort(
          (a, b) =>
            new Date(b.dateTimeIn).getTime() - new Date(a.dateTimeIn).getTime()
        )[0] ?? null
    );
  }, [fromMoveEvents, fromField]);
  const fromMoveInCover = fromOpenEvent?.startDryMatter ?? null;
  const fromMoveInDate = fromOpenEvent?.dateTimeIn
    ? new Date(fromOpenEvent.dateTimeIn)
    : null;
  // Cover field A had before grazing (move-in cover; falls back to its latest
  // reading until the move history loads).
  const fromCoverBefore =
    mode !== "shedToField"
      ? fromMoveInCover ?? fromField?.dryMatterNow ?? null
      : null;

  // Field area (m²→ha) and days grazed (move-in → this move-out).
  const fromAreaHa =
    fromField?.area != null ? fromField.area / M2_PER_HECTARE : null;
  const daysGrazed =
    fromMoveInDate && moveDate
      ? Math.max(
          1,
          Math.round(
            (moveDate.getTime() - fromMoveInDate.getTime()) / 86_400_000
          )
        )
      : null;

  // ── Cover validation (errors that block save) ─────────────────────────────
  // The two covers are on different fields: the residual ("leaving") belongs to
  // field A (FROM), the pre-grazing cover ("arriving") to field B (TO).
  const postNum =
    typeof postGrazingCover === "number" ? postGrazingCover : null;
  const preNum = typeof preGrazingCover === "number" ? preGrazingCover : null;
  const showsPost = mode !== "shedToField"; // FROM is a field
  const showsPre = mode !== "fieldToShed"; // TO is a field
  const postRangeError =
    showsPost &&
    postNum != null &&
    (postNum < COVER_MIN || postNum > COVER_MAX);
  const preRangeError =
    showsPre && preNum != null && (preNum < COVER_MIN || preNum > COVER_MAX);
  // A residual can't be above the cover the field had before grazing.
  const postAboveStartError =
    postNum != null &&
    !postRangeError &&
    fromCoverBefore != null &&
    postNum > fromCoverBefore;
  const hasCoverError = postRangeError || preRangeError || postAboveStartError;
  const submitDisabled = saving || !moveDate || hasCoverError;

  // Sync state when dialog opens with new details
  useEffect(() => {
    if (moveAnimalsDialogDetails.open) {
      postCoverEdited.current = false;
      setFromField(initialFromField ?? null);
      setPostGrazingCover("");
      setPreGrazingCover(toField?.dryMatterNow ?? "");
      setMoveDate(new Date());
      setSaving(false);
    }
  }, [moveAnimalsDialogDetails.open, initialFromField, toField]);

  // Pre-fill the leaving cover (residual) = cover before grazing − expected
  // consumption (daily demand × days ÷ area). Falls back to the post-grazing
  // target until the inputs load. Re-runs as those change, unless the user has
  // already edited the field.
  useEffect(() => {
    if (moveAnimalsDialogDetails.open && !postCoverEdited.current) {
      setPostGrazingCover(
        computeDefaultResidual({
          moveInCover: fromCoverBefore,
          dailyDemand: dailyDemandKgDay,
          daysGrazed,
          areaHa: fromAreaHa,
          target: postGrazingTarget,
        })
      );
    }
  }, [
    moveAnimalsDialogDetails.open,
    fromCoverBefore,
    dailyDemandKgDay,
    daysGrazed,
    fromAreaHa,
    postGrazingTarget,
  ]);

  // On mobile the shared bottom-bar Save/Cancel FABs (rendered by
  // BottomTabNavigation) drive this dialog — same pattern as event dialogs.
  // Broadcasting open state shows the FABs; they click the hidden data-action
  // buttons below.
  useBottomBarFormDialog(moveAnimalsDialogDetails.open);

  function handleClose() {
    closeMoveOutEventDialog();
  }

  async function handleSave() {
    if (!moveDate) return;
    // Surfaced for the mobile FABs (which can't reflect a disabled state).
    if (hasCoverError) {
      notify(t("moveAnimals.validation.fix"), "error");
      return;
    }
    if (mode !== "fieldToShed" && !toField) return;

    setSaving(true);
    analytics.track(AnalyticsEvents.ANIMAL_MOVE_STARTED);

    try {
      // A move out of the source happens at the same instant as the move into
      // the destination, so both readings and the move event share one date.
      const dateIso = moveDate.toISOString();
      const preMoveTasks: Promise<void>[] = [];

      // Post-grazing reading on source field (fieldToField and fieldToShed)
      if (
        (mode === "fieldToField" || mode === "fieldToShed") &&
        fromField &&
        postGrazingCover !== ""
      ) {
        preMoveTasks.push(
          (async () => {
            await createOneReadingWithoutFetchWithInvalidation({
              guid: "new",
              manualDryMatterInput: {
                dryMatterValue: postGrazingCover,
                field: fromField.guid,
                dateTimeMeasurementStart: dateIso,
                dateTimeMeasurementEnd: dateIso,
                event: { eventType: EventType.None },
                kind: "user",
                verificationStatus: "Accepted",
              },
            });
            await deleteAllTilesForField(fromField.guid);
            invalidateFieldData(fromField.guid);
          })()
        );
      }

      // Pre-grazing reading on destination field (fieldToField and shedToField)
      if (
        (mode === "fieldToField" || mode === "shedToField") &&
        toField &&
        preGrazingCover !== ""
      ) {
        preMoveTasks.push(
          (async () => {
            await createOneReadingWithoutFetchWithInvalidation({
              guid: "new",
              manualDryMatterInput: {
                dryMatterValue: preGrazingCover,
                field: toField.guid,
                dateTimeMeasurementStart: dateIso,
                dateTimeMeasurementEnd: dateIso,
                event: { eventType: EventType.None },
                kind: "user",
                verificationStatus: "Accepted",
              },
            });
            await deleteAllTilesForField(toField.guid);
            invalidateFieldData(toField.guid);
          })()
        );
      }

      await Promise.all(preMoveTasks);

      // Execute the move
      if (mode === "fieldToShed" && fromField) {
        await moveToShed(
          fromField.guid,
          moveDate,
          queryClient,
          moveAnimalsDialogDetails.animalGroupGuid
        );
        closeMoveOutEventDialog();
        notify(t("animalGroup.moveToShed.success"));
      } else if (toField) {
        await moveAnimalsIn(toField, fromField ?? undefined, moveDate);
        closeMoveOutEventDialog();
        notify(
          t("fieldlistmenu.notification.moveInSuccess", {
            fieldName: toField.name,
          }),
          "success"
        );
      }
    } catch (e) {
      console.error(e);
      if (mode === "fieldToShed") {
        notify(t("animalGroup.moveToShed.error"), "error");
      } else if (toField) {
        analytics.track(AnalyticsEvents.ANIMAL_MOVE_FAILED);
        notify(
          t("fieldlistmenu.notification.moveInFailed", {
            fieldName: toField.name,
          }),
          "error"
        );
      }
      setSaving(false);
    }
  }

  // Dialog title
  const dialogTitle =
    mode === "fieldToShed"
      ? t("moveAnimals.toShed.title")
      : mode === "shedToField"
      ? `${t("moveAnimals.titlePrefix")} ${animalGroup?.name ?? ""}`
      : `${t("moveAnimals.titlePrefix")} ${animalGroup?.name ?? ""}`;

  const columnSx = {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 1.5,
    // White card sitting on the light-green content background.
    bgcolor: "#fff",
    borderRadius: 2,
    border: "1px solid #E4EAD8",
    p: 2,
  } as const;

  // Shed card: vertically centre the shed node (no cover input to fill height).
  const shedColumnSx = { ...columnSx, justifyContent: "center" } as const;

  // Fixed-height name header so the cover/date rows line up across both columns
  // (the FROM side carries an extra "Change" button).
  const nameHeaderSx = {
    width: "100%",
    minHeight: 48,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.25,
  } as const;
  const nameTextSx = {
    color: NAME_COLOR,
    textAlign: "center",
    lineHeight: 1.2,
  } as const;

  // ── DM utilised summary ───────────────────────────────────────────────────
  // (postNum, fromCoverBefore, the cover errors and submitDisabled are computed
  // above.) Dry matter the herd utilised on field A (kg DM/ha and total kg DM).
  const dmUtilisedPerHa =
    postNum != null &&
    !postRangeError &&
    fromCoverBefore != null &&
    fromCoverBefore > postNum
      ? fromCoverBefore - postNum
      : null;
  const dmUtilisedTotal =
    postNum != null && !postRangeError && fromCoverBefore != null && fromAreaHa
      ? dmUtilisedTotalKg(fromCoverBefore, postNum, fromAreaHa)
      : null;

  // Intake per head per day (daysGrazed computed above).
  const kgPerHeadPerDay =
    dmUtilisedTotal != null && headCount > 0 && daysGrazed != null
      ? dmUtilisedTotal / headCount / daysGrazed
      : null;

  // Grazed, but barely — likely a mistyped residual.
  const lowUtilisationWarning =
    dmUtilisedPerHa != null &&
    dmUtilisedPerHa > 0 &&
    dmUtilisedPerHa < SIGNIFICANT_GAP;

  return (
    <Dialog
      open={moveAnimalsDialogDetails.open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 1.5, sm: 4 },
          width: "100%",
          maxWidth: { xs: "100%", sm: 680 },
        },
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/* Header (single row): title · move date · primary action · close */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            py: 1.5,
          }}
        >
          <Box
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: "1rem",
              fontWeight: 600,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {mode === "fieldToShed" ? (
              dialogTitle
            ) : (
              <>
                <Box
                  component="span"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 400,
                    display: { xs: "none", sm: "inline" },
                  }}
                >
                  {t("moveAnimals.titlePrefix")}{" "}
                </Box>
                {animalGroup?.name ?? ""}
              </>
            )}
          </Box>
          <MobileDateTimePicker
            enableAccessibleFieldDOMStructure={false}
            open={dateOpen}
            onOpen={() => setDateOpen(true)}
            onClose={() => setDateOpen(false)}
            format="MMM d, HH:mm"
            ampm={false}
            value={moveDate}
            onChange={(value: Date | null) => setMoveDate(value)}
            slotProps={{
              textField: {
                variant: "outlined",
                size: "small",
                onClick: () => setDateOpen(true),
                inputProps: {
                  "aria-label": t("moveAnimals.date.label"),
                  readOnly: true,
                },
                sx: {
                  width: 178,
                  flexShrink: 0,
                  cursor: "pointer",
                  // Match the Save button: 8px radius, 31px tall.
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: 31,
                  },
                  "& input": {
                    cursor: "pointer",
                    color: NAME_COLOR,
                    fontWeight: 500,
                    fontSize: "0.8125rem",
                    py: 0,
                  },
                  "& .MuiInputAdornment-root .MuiIconButton-root": {
                    p: "2px",
                  },
                  "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                    fontSize: 18,
                  },
                },
              },
            }}
          />
          {/* On mobile, Save/close move to the bottom-bar FABs. */}
          <Button
            onClick={handleSave}
            variant="contained"
            size="small"
            disabled={submitDisabled}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              flexShrink: 0,
              textTransform: "none",
              backgroundColor: "#2E7831",
              "&:hover": { backgroundColor: "#1C4321" },
            }}
          >
            {t("moveAnimals.submit")}
          </Button>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label={t("moveAnimals.cancel")}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              flexShrink: 0,
              bgcolor: "#F1EFF7",
              color: "#7D5DA3",
              "&:hover": { bgcolor: "#E5E1F0" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "#F0F0F0" }} />
        <DialogContent sx={{ p: 3, backgroundColor: "#F7FCF1" }}>
          {/* From → To columns (equal-height cards; stacked on mobile) */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "stretch",
              gap: 2,
            }}
          >
            {/* FROM column */}
            <Box sx={mode === "shedToField" ? shedColumnSx : columnSx}>
              {mode === "shedToField" ? (
                <ShedNode label={t("moveAnimals.shed")} />
              ) : (
                <>
                  <Stack alignItems="center" spacing={1} sx={{ width: "100%" }}>
                    <FieldThumb
                      geom={fromField?.geom}
                      dryMatterNow={fromField?.dryMatterNow}
                    />
                    <Box sx={nameHeaderSx}>
                      <Typography variant="subtitle2" sx={nameTextSx}>
                        {mode === "fieldToField"
                          ? fromField?.name
                          : initialFromField?.name}
                      </Typography>
                      {mode === "fieldToField" && groupFields.length > 1 && (
                        <Button
                          size="small"
                          onClick={(e) => setFromAnchor(e.currentTarget)}
                          sx={{
                            minWidth: "auto",
                            p: 0,
                            fontSize: "0.7rem",
                            lineHeight: 1.2,
                            textTransform: "none",
                            color: "text.secondary",
                            textDecoration: "underline",
                            "&:hover": {
                              backgroundColor: "transparent",
                              color: NAME_COLOR,
                            },
                          }}
                        >
                          {t("moveAnimals.change")}
                        </Button>
                      )}
                    </Box>
                  </Stack>
                  {mode === "fieldToField" && (
                    <Menu
                      anchorEl={fromAnchor}
                      open={Boolean(fromAnchor)}
                      onClose={() => setFromAnchor(null)}
                    >
                      {groupFields.map((f) => (
                        <MenuItem
                          key={f.guid}
                          selected={f.guid === fromField?.guid}
                          onClick={() => {
                            postCoverEdited.current = false;
                            setFromField(f);
                            setFromAnchor(null);
                          }}
                        >
                          {f.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  )}

                  <Box sx={{ width: "100%" }}>
                    <Typography sx={coverLabelSx(LEAVING_ACCENT)}>
                      {t("moveAnimals.coverLeaving.label")}
                    </Typography>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      value={postGrazingCover}
                      error={postRangeError || postAboveStartError}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        postCoverEdited.current = true;
                        setPostGrazingCover(
                          e.target.value === "" ? "" : Number(e.target.value)
                        );
                      }}
                      disabled={mode === "fieldToField" && !fromField}
                      slotProps={{
                        htmlInput: {
                          "aria-label": t("moveAnimals.coverLeaving.label"),
                        },
                        input: {
                          endAdornment: (
                            <InputAdornment position="end" sx={{ gap: 0.75 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t("addCovers.single.unit")}
                              </Typography>
                              {postGrazingTarget != null &&
                                (mode === "fieldToShed" || fromField) && (
                                  <TargetChip
                                    accent={LEAVING_ACCENT}
                                    bg="#FBF7EC"
                                    hoverBg="#F4E9CC"
                                    label={t("moveAnimals.useTarget", {
                                      value: postGrazingTarget,
                                    })}
                                    onClick={() => {
                                      postCoverEdited.current = true;
                                      setPostGrazingCover(postGrazingTarget);
                                    }}
                                  />
                                )}
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                    {postRangeError && (
                      <CoverMessage
                        severity="error"
                        text={t("moveAnimals.validation.range", {
                          min: COVER_MIN,
                          max: COVER_MAX,
                        })}
                      />
                    )}
                    {postAboveStartError && fromCoverBefore != null && (
                      <CoverMessage
                        severity="error"
                        text={t("moveAnimals.validation.belowStart", {
                          value: fromCoverBefore,
                        })}
                      />
                    )}
                    {lowUtilisationWarning && (
                      <CoverMessage
                        severity="warning"
                        text={t("moveAnimals.validation.lowUtilisation")}
                      />
                    )}
                  </Box>
                  <LeafChip cover={postGrazingCover} />
                  {dmUtilisedTotal != null && dmUtilisedTotal > 0 && (
                    <Box
                      sx={{
                        alignSelf: "center",
                        textAlign: "center",
                        maxWidth: 260,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.25,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: NAME_COLOR, fontWeight: 700 }}
                      >
                        {kgPerHeadPerDay != null
                          ? t("moveAnimals.dmUtilisedWithHead", {
                              value: dmUtilisedTotal.toLocaleString(),
                              perHead: kgPerHeadPerDay.toFixed(1),
                            })
                          : t("moveAnimals.dmUtilised", {
                              value: dmUtilisedTotal.toLocaleString(),
                            })}
                      </Typography>
                      {fromCoverBefore != null && daysGrazed != null && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ lineHeight: 1.3 }}
                        >
                          {t("moveAnimals.grazingStarted", {
                            pre: fromCoverBefore.toLocaleString(),
                            days: daysGrazed,
                          })}
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Box>

            {/* Arrow (points right on desktop, down when cards stack) */}
            <Box
              sx={{
                alignSelf: "center",
                color: "#2E7831",
                fontSize: 28,
                fontWeight: 700,
                px: 0.5,
                transform: { xs: "rotate(90deg)", sm: "none" },
              }}
            >
              →
            </Box>

            {/* TO column */}
            <Box sx={mode === "fieldToShed" ? shedColumnSx : columnSx}>
              {mode === "fieldToShed" ? (
                <ShedNode label={t("moveAnimals.shed")} />
              ) : (
                <>
                  <Stack alignItems="center" spacing={1} sx={{ width: "100%" }}>
                    <FieldThumb
                      geom={toField?.geom}
                      dryMatterNow={toField?.dryMatterNow}
                    />
                    <Box sx={nameHeaderSx}>
                      <Typography variant="subtitle2" sx={nameTextSx}>
                        {toField?.name}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ width: "100%" }}>
                    <Typography sx={coverLabelSx(ARRIVING_ACCENT)}>
                      {t("moveAnimals.coverArriving.label")}
                    </Typography>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      type="number"
                      value={preGrazingCover}
                      error={preRangeError}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setPreGrazingCover(
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      slotProps={{
                        htmlInput: {
                          "aria-label": t("moveAnimals.coverArriving.label"),
                        },
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {t("addCovers.single.unit")}
                              </Typography>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                    {preRangeError && (
                      <CoverMessage
                        severity="error"
                        text={t("moveAnimals.validation.range", {
                          min: COVER_MIN,
                          max: COVER_MAX,
                        })}
                      />
                    )}
                  </Box>
                  <LeafChip cover={preGrazingCover} />
                </>
              )}
            </Box>
          </Box>
        </DialogContent>

        {/* Hidden actions clicked by the mobile bottom-bar FABs
            (red ✕ → cancel, green ✓ → save). Found by data-action. */}
        <Button
          data-action="dialog-cancel"
          onClick={handleClose}
          sx={{ display: "none" }}
        >
          {t("moveAnimals.cancel")}
        </Button>
        <Button
          data-action="dialog-save"
          aria-label={t("moveAnimals.submit")}
          onClick={handleSave}
          disabled={saving}
          sx={{ display: "none" }}
        />
      </LocalizationProvider>
    </Dialog>
  );
}
